"use client";

import { DETAIL_DESKTOP } from "@/components/shell/detail-page-desktop";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { cn } from "@/lib/utils";
import type { Hiive, MembershipState } from "./hiive-page.types";

/**
 * Everything this footer points at is public.
 *
 * It used to open the sign-in drawer for a signed-out viewer instead of
 * navigating, which meant nobody could see what a hiive costs — or which tier
 * an event needs — without an account first. That is exactly backwards for a
 * link shared on social media: the price grid IS the pitch, and the sign-in
 * belongs at the moment somebody chooses a tier, not before they have seen one.
 * The tiers page carries its own auth gate on the join button.
 */

interface HiiveFooterCtaProps {
  hiive: Hiive;
  hiiveSlug: string;
  membershipState: MembershipState;
  /**
   * `floating` is the phone's pill pinned over the bottom of the screen;
   * `inline` is the desktop card under the hero, in the sticky media column.
   */
  variant?: "floating" | "inline";
}

const getButtonText = (membershipState: MembershipState) => {
  switch (membershipState) {
    case "not_member":
      return "Join Hiive";
    case "free_member":
      return "View Membership";
    case "free_member_upgrade_available":
      // "Upgrade Plan" only described one of the things this button does. The
      // tiers page it opens is where you change plan, see what your current one
      // costs, or leave — "manage" is the honest verb for all three.
      return "Manage membership";
    case "payment_pending":
      return "Complete Payment";
    case "paid_member":
      return "View Membership";
    case "pending_member":
      return "View Request";
    default:
      return "Join Hiive";
  }
};

const getButtonHref = (membershipState: MembershipState, hiiveSlug: string) => {
  if (membershipState === "pending_member") {
    return `/hiives/${hiiveSlug}/join`;
  }
  if (membershipState === "free_member" || membershipState === "paid_member") {
    return `/wallet/hiives#hiive-${hiiveSlug}`;
  }
  return `/hiives/${hiiveSlug}/join`;
};

export function HiiveFooterCta({
  hiive,
  hiiveSlug,
  membershipState,
  variant = "floating",
}: HiiveFooterCtaProps) {
  const buttonText = getButtonText(membershipState);
  const buttonHref = getButtonHref(membershipState, hiiveSlug);

  const trackClick = () => {
    captureClientEvent(ANALYTICS_EVENTS.HIIVE_CTA_CLICKED, {
      hiive_id: hiive.id,
      hiive_name: hiive.name,
      hiive_slug: hiiveSlug,
      membership_state: membershipState,
      button_text: buttonText,
    });
    if (membershipState === "not_member") {
      captureClientEvent(ANALYTICS_EVENTS.HIIVE_JOIN_STARTED, {
        hiive_id: hiive.id,
        hiive_name: hiive.name,
        hiive_slug: hiiveSlug,
      });
    }
  };

  const buttonClassName = cn(
    "rounded-full border border-border bg-surface-elevated px-8 py-2 text-center text-lg font-semibold text-text-primary shadow-sm transition-all hover:scale-105 hover:bg-surface-alt",
    "dark:border-0 dark:bg-white dark:text-black dark:shadow-lg dark:hover:bg-white/90",
  );

  if (variant === "inline") {
    return (
      <div className={DETAIL_DESKTOP.actionCard}>
        <BackAwareLink
          href={buttonHref}
          onClick={trackClick}
          className={cn(
            "block w-full",
            buttonClassName,
            // No scale on a card that sits beside other content — it nudges
            // the neighbouring column, and hover is the only state here.
            "hover:scale-100",
            // The media column is ~200pt on a short landscape screen (iPhone
            // Duo closed and rotated): the pill's px-8 wrapped the label.
            "px-4 short:text-base",
          )}
        >
          {buttonText}
        </BackAwareLink>
      </div>
    );
  }

  return (
    // `wide:hidden`: the server renders this before it knows the width, and a
    // desktop mounts the inline card instead once it does. Pinned between the
    // side safe areas (iPhone Duo's outer display has a side status bar).
    <div className="fixed right-[env(safe-area-inset-right,0px)] bottom-4 left-[env(safe-area-inset-left,0px)] z-1000 mx-auto max-w-4xl px-4 wide:hidden">
      <div className="relative overflow-hidden rounded-full border border-border bg-surface/92 px-3 py-2 shadow-[0_12px_28px_rgba(15,23,42,0.12)] backdrop-blur-xl dark:border-glass-border-20 dark:bg-glass-bg-05 dark:shadow-2xl">
        <div className="pointer-events-none absolute inset-0 bg-linear-to-r from-background/60 to-transparent dark:from-white/5" />

        <div className="relative flex w-full items-center justify-center">
          <BackAwareLink
            href={buttonHref}
            onClick={trackClick}
            className={cn("w-full", buttonClassName)}
          >
            {buttonText}
          </BackAwareLink>
        </div>
      </div>
    </div>
  );
}
